import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Card, CircularProgress, Dialog, IconButton, Tooltip, Typography } from '@mui/material';
import { Close as CloseIcon, InsertDriveFileOutlined as FileIcon } from '@mui/icons-material';
import { colors } from 'Constants/Colors';
import useTranslation from 'Hooks/useTranslation';
import { s3 } from 'Services/AWS/s3';
import { formatFileSize, isImageEvidence } from '../utils/evidence-validation';

const styles = {
  list: { mt: 1, display: 'flex', flexDirection: 'column', gap: 0.75 },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.25,
    px: 1.5,
    py: 1,
    bgcolor: 'rgb(245, 246, 250)',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'background-color .15s ease',
    '&:hover': { bgcolor: 'rgb(238, 240, 246)' },
  },
  rowDisabled: { cursor: 'default', opacity: 0.7, '&:hover': { bgcolor: 'rgb(245, 246, 250)' } },
  fileIcon: { fontSize: 18, color: '#1FBF6F', flexShrink: 0 },
  fileName: {
    fontSize: 13,
    color: 'rgb(31, 42, 58)',
    flex: 1,
    minWidth: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  size: { fontSize: 11.5, color: 'rgb(138, 147, 166)', flexShrink: 0 },
  closeBtn: { p: 0.25, ml: 0.25 },
  closeIcon: { fontSize: 16, color: 'rgb(138, 147, 166)' },
  preview: { '& .MuiDialog-paper': { bgcolor: colors.BLACK_50, boxShadow: 'none', overflow: 'hidden' } },
  previewImg: { maxWidth: '100%', maxHeight: '90vh', display: 'block', margin: 'auto' },
  emptyCard: { p: 3, textAlign: 'center', color: colors.GRAY_DARK, fontSize: 13 },
};

const useSignedUrls = evidences => {
  const [urls, setUrls] = useState({});

  useEffect(() => {
    let cancelled = false;
    const missing = evidences.filter(e => !urls[e.id]);
    if (missing.length === 0) return undefined;

    (async () => {
      const entries = await Promise.all(
        missing.map(async e => {
          try {
            const response = await s3.getSignedUrl(e.fileUrl);
            return [e.id, response?.signedUrl || null];
          } catch (err) {
            return [e.id, null];
          }
        })
      );
      if (!cancelled) {
        setUrls(prev => {
          const next = { ...prev };
          entries.forEach(([id, url]) => {
            next[id] = url;
          });
          return next;
        });
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evidences]);

  return urls;
};

// Excel/Word/PowerPoint signed URLs are served with `Content-Disposition: attachment`,
// so `window.open` would spawn a blank tab and then trigger the download in it.
// We download them directly via an anchor instead, leaving previews (image/pdf) on tabs/modal.
const triggerDownload = (url, filename) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || '';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Pure presentational list of evidences. The component looks up signed URLs on
 * its own (S3 keys are not public) but delegates delete to the parent.
 */
export default function EvidencesGrid({ evidences, canDelete, onDelete }) {
  const { t2 } = useTranslation('EditCourseSessions');
  const urls = useSignedUrls(evidences || []);
  const [previewUrl, setPreviewUrl] = useState(null);

  const handlePreview = useCallback(
    evidence => {
      const url = urls[evidence.id];
      if (!url) return;
      if (isImageEvidence(evidence.fileType)) {
        setPreviewUrl(url);
      } else if (evidence.fileType === 'application/pdf') {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        triggerDownload(url, evidence.fileName);
      }
    },
    [urls]
  );

  if (!evidences || evidences.length === 0) {
    return (
      <Card sx={styles.emptyCard} elevation={0}>
        <Typography sx={{ fontSize: 13, color: colors.GRAY_DARK }}>{t2('empty.noEvidences')}</Typography>
      </Card>
    );
  }

  return (
    <Box sx={styles.list}>
      {evidences.map(e => {
        const url = urls[e.id];
        const rowSx = url ? styles.row : { ...styles.row, ...styles.rowDisabled };
        return (
          <Box key={e.id} sx={rowSx} onClick={() => handlePreview(e)}>
            <FileIcon sx={styles.fileIcon} />
            <Tooltip title={e.fileName}>
              <Typography sx={styles.fileName}>{e.fileName}</Typography>
            </Tooltip>
            <Typography sx={styles.size}>{formatFileSize(e.fileSize)}</Typography>
            {!url && <CircularProgress size={12} sx={{ color: colors.GRAY_DARK, ml: 0.5 }} />}
            {canDelete && (
              <Tooltip title={t2('evidences.remove')}>
                <IconButton
                  size="small"
                  sx={styles.closeBtn}
                  onClick={event => {
                    event.stopPropagation();
                    onDelete(e);
                  }}
                >
                  <CloseIcon sx={styles.closeIcon} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      })}

      <Dialog open={!!previewUrl} onClose={() => setPreviewUrl(null)} maxWidth="lg" sx={styles.preview}>
        <IconButton
          onClick={() => setPreviewUrl(null)}
          sx={{ position: 'absolute', top: 8, right: 8, color: '#fff', zIndex: 1 }}
        >
          <CloseIcon />
        </IconButton>
        {previewUrl && <Box component="img" src={previewUrl} alt="preview" sx={styles.previewImg} />}
      </Dialog>
    </Box>
  );
}

EvidencesGrid.propTypes = {
  evidences: PropTypes.array,
  canDelete: PropTypes.bool,
  onDelete: PropTypes.func.isRequired,
};

EvidencesGrid.defaultProps = { evidences: [], canDelete: false };
